import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { CheckCircle, Loader2, Save, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';

export function AcceptedMembersPlugin({ serverId }: { serverId: string }) {
  const { t } = useI18n();
  const { data: plugins = [] } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['servers', serverId, 'roles'],
    queryFn: () => serverApi.getRoles(serverId),
  });

  const acceptedMembersPlugin = (plugins as any[]).find((p: any) => p.pluginId === 'accepted-members');
  const legacyWelcomePlugin = (plugins as any[]).find((p: any) => p.pluginId === 'welcome');
  const acceptedMembersEnabled = !!acceptedMembersPlugin?.enabled;

  const { mutate: toggleAcceptedMembers, isPending: togglingAcceptedMembers } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'accepted-members') : pluginApi.disablePlugin(serverId, 'accepted-members'),
    {
      onSuccess: () => toast.success(t('accepted.pluginUpdatedToast')),
      invalidateQueries: [['servers', serverId, 'plugins']],
    }
  );

  const { mutate: saveAcceptedMembersConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'accepted-members', config),
    {
      onSuccess: () => toast.success(t('accepted.savedToast')),
      invalidateQueries: [['servers', serverId, 'plugins']],
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: false,
    adminChannelId: '',
    verifiedRoleId: 'none',
    timeoutSeconds: 300,
    declineReason: 'Join request declined by staff',
    sendWelcomeOnAccept: true,
  });

  useEffect(() => {
    if (acceptedMembersPlugin?.config) {
      setConfig((prev: any) => ({
        ...prev,
        ...acceptedMembersPlugin.config,
      }));
      return;
    }

    const legacy = legacyWelcomePlugin?.config?.acceptedMembers;
    if (legacy && typeof legacy === 'object') {
      setConfig((prev: any) => ({
        ...prev,
        ...legacy,
      }));
    }
  }, [acceptedMembersPlugin, legacyWelcomePlugin]);

  const handleSave = () => {
    saveAcceptedMembersConfig(config);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-emerald-500/20 rounded-xl">
            <Users className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">{t('accepted.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('accepted.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Label>{t('accepted.pluginToggle')}</Label>
          <Switch
            checked={acceptedMembersEnabled}
            onCheckedChange={(enabled) => toggleAcceptedMembers({ enabled })}
            disabled={togglingAcceptedMembers}
          />
        </div>
      </div>

      <div className="glass-card p-6 space-y-5">
        <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
          <div>
            <Label>{t('accepted.flowTitle')}</Label>
            <p className="text-xs text-muted-foreground">{t('accepted.flowDesc')}</p>
          </div>
          <Switch
            checked={!!config.enabled}
            onCheckedChange={(checked) =>
              setConfig({
                ...config,
                enabled: checked,
              })
            }
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>{t('accepted.adminChannel')}</Label>
            <Select
              value={config.adminChannelId || ''}
              onValueChange={(value) =>
                setConfig({
                  ...config,
                  adminChannelId: value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('accepted.selectChannel')} />
              </SelectTrigger>
              <SelectContent>
                {channels.map((ch: any) => (
                  <SelectItem key={ch.id} value={ch.id}>#{ch.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{t('accepted.verifiedRole')}</Label>
            <Select
              value={config.verifiedRoleId || 'none'}
              onValueChange={(value) =>
                setConfig({
                  ...config,
                  verifiedRoleId: value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('accepted.selectRole')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('accepted.none')}</SelectItem>
                {roles.map((role: any) => (
                  <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>{t('accepted.timeoutSeconds')}</Label>
            <Input
              type="number"
              min={30}
              value={config.timeoutSeconds || 300}
              onChange={(e) =>
                setConfig({
                  ...config,
                  timeoutSeconds: Math.max(30, Number(e.target.value) || 300),
                })
              }
            />
          </div>

          <div>
            <Label>{t('accepted.declineReason')}</Label>
            <Input
              value={config.declineReason || ''}
              onChange={(e) =>
                setConfig({
                  ...config,
                  declineReason: e.target.value,
                })
              }
              placeholder="Join request declined by staff"
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
          <div>
            <Label>{t('accepted.sendWelcome')}</Label>
            <p className="text-xs text-muted-foreground">{t('accepted.sendWelcomeDesc')}</p>
          </div>
          <Switch
            checked={config.sendWelcomeOnAccept !== false}
            onCheckedChange={(checked) =>
              setConfig({
                ...config,
                sendWelcomeOnAccept: checked,
              })
            }
          />
        </div>

        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {t('accepted.dashboardHint')}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="min-w-[160px]">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {t('accepted.save')}
        </Button>
      </div>
    </div>
  );
}
